import { useRouter } from 'next/router';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import ChevronLeft from '~components/Icon/ChevronLeft';
import ChevronRight from '~components/Icon/ChevronRight';
import useIntersectionObserver from '~hooks/useIntersectionObserver';
import useMedia from '~hooks/useMedia';
import { IndustryItem } from '~types/company';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';
import { isClient } from '~utils/lodash/isServer';

import * as S from './style';

interface Props {
  selectedMenuId?: string;
  menuList: IndustryItem[];
  onClick?: (sectionCode: string) => void;
}

export default function SubCategoryTabs({ selectedMenuId, menuList, onClick }: Props) {
  const router = useRouter();
  const { isDesktop } = useMedia();
  const menuListRef = useRef<HTMLDivElement>(null);
  const startPositionRef = useRef<HTMLDivElement>(null);
  const endPositionRef = useRef<HTMLDivElement>(null);
  const [displayArrowPrev, setDisplayArrowPrev] = useState<boolean>();
  const [displayArrowNext, setDisplayArrowNext] = useState<boolean>();

  const startPositionEntry = useIntersectionObserver(startPositionRef, {
    root: menuListRef.current,
  });
  const endPositionEntry = useIntersectionObserver(endPositionRef, {
    root: menuListRef.current,
  });

  const handleClick = (_selectedMenu: IndustryItem) => {
    if (_selectedMenu.code === selectedMenuId) {
      return;
    }

    setGtmDataLayer('home__companyIndustry__click', {
      companyIndustry: _selectedMenu.name,
    });

    if (onClick) {
      onClick(_selectedMenu.code);
    }
  };

  const handleAnimateScroll = (elem: HTMLElement) => {
    const { offsetLeft, offsetWidth } = elem;

    if (menuListRef.current) {
      const spacingForCenter = (menuListRef.current?.offsetWidth - offsetWidth) / 2;
      menuListRef.current.scrollTo({ left: offsetLeft - spacingForCenter, behavior: 'smooth' });
    }
  };

  const handleClickArrow = (direction?: 'prev' | 'next') => {
    if (menuListRef.current) {
      const target = menuListRef.current;
      const moveValue = direction === 'next' ? 300 : -300;

      target.scrollTo({ left: target.scrollLeft + moveValue, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setDisplayArrowPrev(isDesktop && !startPositionEntry?.isIntersecting);
    setDisplayArrowNext(isDesktop && !endPositionEntry?.isIntersecting);
  }, [isDesktop, startPositionEntry, endPositionEntry]);

  useEffect(() => {
    if (menuListRef.current) {
      menuListRef.current.scrollTo({ left: 0 });
    }
  }, [menuList]);

  useEffect(() => {
    if (router.query && isClient) {
      const elem = document.querySelector(
        `#sub-category-id-${router.query.subCategory}`,
      ) as HTMLElement;

      if (elem) {
        handleAnimateScroll(elem);
      }
    }
  }, [router.query]);

  return (
    <S.MenuWrapper>
      {displayArrowPrev && (
        <S.ArrowPrev onClick={() => handleClickArrow('prev')}>
          <ChevronLeft />
        </S.ArrowPrev>
      )}
      <S.MenuList ref={menuListRef}>
        <S.IntersectionHolder ref={startPositionRef} />
        {menuList.map((item) => (
          <S.MenuItem
            key={item.code}
            onClick={(e) => handleClick(item)}
            id={`sub-category-id-${item.code}`}
            $selected={selectedMenuId === item.code}>
            {item.name}
          </S.MenuItem>
        ))}
        <S.IntersectionHolder ref={endPositionRef} />
      </S.MenuList>
      {displayArrowNext && (
        <S.ArrowNext onClick={() => handleClickArrow('next')}>
          <ChevronRight />
        </S.ArrowNext>
      )}
    </S.MenuWrapper>
  );
}
